import { useEffect, useRef, useState } from 'react';
import { electronicFormatIBAN, friendlyFormatIBAN, isValidIBAN } from 'ibantools';
import { ExternalLink, Plus, Trash2, Upload } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/frontend/components/ui/card";
import { Button } from "@/frontend/components/ui/button";
import { Input } from "@/frontend/components/ui/input";
import { Label } from "@/frontend/components/ui/label";
import { Switch } from "@/frontend/components/ui/switch";
import { Textarea } from "@/frontend/components/ui/textarea";
import { useEnergySettings, useUpdateEnergySettings } from '@/backend/hooks/admin/useEnergySettings';
import { usePaymentSettings, useUpdatePaymentSettings } from '@/backend/hooks/admin/usePaymentSettings';
import { usePolicySettings, useUploadPolicy } from '@/backend/hooks/admin/usePolicySettings';
import { useContactSettings, useUpdateContactSettings } from '@/backend/hooks/admin/useContactSettings';
import type { AdminAlertEmail } from '@/backend/services/energySettings';
import { DEFAULT_PAYMENT_SETTINGS, type PaymentSettings } from '@/backend/services/paymentSettings';
import { DEFAULT_CONTACT_SETTINGS, type ContactSettings } from '@/backend/services/contactSettings';
import type { PolicyKey, PolicySettings } from '@/shared/types/policySettings';
import { useToast } from '@/backend/hooks/use-toast';
import { useAdminAuth } from '@/frontend/contexts/AdminAuthContext';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (email: string) => EMAIL_REGEX.test(email.trim());
const MAX_ALERT_EMAILS = 5;

type PaymentFormState = Pick<
  PaymentSettings,
  'iban' | 'accountHolder' | 'bankName' | 'paymentInstructions' | 'firstPaymentLabel'
>;

const toPaymentForm = (settings: PaymentSettings | undefined): PaymentFormState => ({
  iban: friendlyFormatIBAN(settings?.iban ?? DEFAULT_PAYMENT_SETTINGS.iban) ?? '',
  accountHolder: settings?.accountHolder ?? DEFAULT_PAYMENT_SETTINGS.accountHolder,
  bankName: settings?.bankName ?? DEFAULT_PAYMENT_SETTINGS.bankName,
  paymentInstructions: settings?.paymentInstructions ?? DEFAULT_PAYMENT_SETTINGS.paymentInstructions,
  firstPaymentLabel: settings?.firstPaymentLabel ?? DEFAULT_PAYMENT_SETTINGS.firstPaymentLabel,
});

type ContactFormState = Pick<
  ContactSettings,
  'addressLine1' | 'addressLine2' | 'phone' | 'email' | 'officeHours'
>;

const toContactForm = (settings: ContactSettings | undefined): ContactFormState => ({
  addressLine1: settings?.addressLine1 ?? DEFAULT_CONTACT_SETTINGS.addressLine1,
  addressLine2: settings?.addressLine2 ?? DEFAULT_CONTACT_SETTINGS.addressLine2,
  phone: settings?.phone ?? DEFAULT_CONTACT_SETTINGS.phone,
  email: settings?.email ?? DEFAULT_CONTACT_SETTINGS.email,
  officeHours: settings?.officeHours ?? DEFAULT_CONTACT_SETTINGS.officeHours,
});

const AdminSettings = () => {
  const { toast } = useToast();
  const { user } = useAdminAuth();
  const { data: settings, isLoading } = useEnergySettings();
  const updateSettings = useUpdateEnergySettings();
  const { data: paymentSettings, isLoading: isLoadingPayment } = usePaymentSettings();
  const updatePaymentSettings = useUpdatePaymentSettings();
  const [paymentForm, setPaymentForm] = useState<PaymentFormState>(() => toPaymentForm(paymentSettings));

  useEffect(() => {
    if (paymentSettings) setPaymentForm(toPaymentForm(paymentSettings));
  }, [paymentSettings]);

  const handlePaymentChange = (field: keyof PaymentFormState, value: string) => {
    setPaymentForm((prev) => ({ ...prev, [field]: value }));
  };

  const electronicIban = electronicFormatIBAN(paymentForm.iban) ?? '';
  const trimmedHolder = paymentForm.accountHolder.trim();
  const trimmedBank = paymentForm.bankName.trim();
  const ibanIsValid = electronicIban !== '' && isValidIBAN(electronicIban);
  const showIbanError = paymentForm.iban.trim() !== '' && !ibanIsValid;
  const canSavePayment =
    ibanIsValid &&
    trimmedHolder !== '' &&
    trimmedBank !== '' &&
    paymentForm.firstPaymentLabel.trim() !== '' &&
    !updatePaymentSettings.isPending;

  const handleSavePayment = async () => {
    if (!canSavePayment) {
      toast({
        title: 'Validation error',
        description: ibanIsValid
          ? 'Account holder, bank name, and first-payment label are required'
          : 'Please enter a valid IBAN',
        variant: 'destructive',
      });
      return;
    }
    try {
      await updatePaymentSettings.mutateAsync({
        data: {
          iban: electronicIban,
          accountHolder: trimmedHolder,
          bankName: trimmedBank,
          paymentInstructions: paymentForm.paymentInstructions,
          firstPaymentLabel: paymentForm.firstPaymentLabel.trim(),
        },
        updatedBy: user?.email ?? user?.uid ?? 'unknown',
      });
      toast({ title: 'Payment settings saved', description: 'Payment instructions updated successfully' });
    } catch {
      toast({ title: 'Error', description: 'Failed to save payment settings', variant: 'destructive' });
    }
  };

  const { data: contactSettings, isLoading: isLoadingContact } = useContactSettings();
  const updateContactSettings = useUpdateContactSettings();
  const [contactForm, setContactForm] = useState<ContactFormState>(() => toContactForm(contactSettings));

  useEffect(() => {
    if (contactSettings) setContactForm(toContactForm(contactSettings));
  }, [contactSettings]);

  const handleContactChange = (field: keyof ContactFormState, value: string) => {
    setContactForm((prev) => ({ ...prev, [field]: value }));
  };

  const contactEmailValid = isValidEmail(contactForm.email);
  const showContactEmailError = contactForm.email.trim() !== '' && !contactEmailValid;
  const canSaveContact =
    contactForm.addressLine1.trim() !== '' &&
    contactForm.addressLine2.trim() !== '' &&
    contactForm.phone.trim() !== '' &&
    contactEmailValid &&
    !updateContactSettings.isPending;

  const handleSaveContact = async () => {
    if (!canSaveContact) {
      toast({
        title: 'Validation error',
        description: contactEmailValid
          ? 'Address lines and phone are required'
          : 'Please enter a valid email',
        variant: 'destructive',
      });
      return;
    }
    try {
      await updateContactSettings.mutateAsync({
        data: {
          addressLine1: contactForm.addressLine1.trim(),
          addressLine2: contactForm.addressLine2.trim(),
          phone: contactForm.phone.trim(),
          email: contactForm.email.trim(),
          officeHours: contactForm.officeHours.trim(),
        },
        updatedBy: user?.email ?? user?.uid ?? 'unknown',
      });
      toast({ title: 'Contact details saved', description: 'Footer and contact page updated successfully' });
    } catch {
      toast({ title: 'Error', description: 'Failed to save contact details', variant: 'destructive' });
    }
  };

  const { data: policySettings, isLoading: isLoadingPolicies } = usePolicySettings();
  const uploadPolicy = useUploadPolicy();
  const cancellationInputRef = useRef<HTMLInputElement>(null);
  const landlordInputRef = useRef<HTMLInputElement>(null);
  const [uploadingKey, setUploadingKey] = useState<PolicyKey | null>(null);

  const handlePolicyFileChange = async (
    key: PolicyKey,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (file.type !== 'application/pdf') {
      toast({ title: 'Invalid file', description: 'Only PDF files are accepted', variant: 'destructive' });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'File must be smaller than 10MB', variant: 'destructive' });
      return;
    }

    setUploadingKey(key);
    try {
      await uploadPolicy.mutateAsync({
        key,
        file,
        updatedBy: user?.email ?? user?.uid ?? 'unknown',
      });
      toast({
        title: 'Policy uploaded',
        description: `${key === 'cancellation' ? 'Cancellation' : 'Landlord'} policy updated`,
      });
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: err instanceof Error ? err.message : 'Failed to upload policy',
        variant: 'destructive',
      });
    } finally {
      setUploadingKey(null);
    }
  };

  // null = no local edit; falls back to server value
  const [localEmails, setLocalEmails] = useState<AdminAlertEmail[] | null>(null);
  const emails = localEmails ?? settings?.adminAlertEmails ?? [];

  const updateEmails = (next: AdminAlertEmail[]) => setLocalEmails(next);

  const handleEmailChange = (index: number, value: string) => {
    updateEmails(emails.map((e, i) => (i === index ? { ...e, email: value } : e)));
  };

  const handleToggle = (index: number, enabled: boolean) => {
    updateEmails(emails.map((e, i) => (i === index ? { ...e, enabled } : e)));
  };

  const handleRemove = (index: number) => {
    updateEmails(emails.filter((_, i) => i !== index));
  };

  const handleAdd = () => {
    if (emails.length >= MAX_ALERT_EMAILS) return;
    updateEmails([...emails, { email: '', enabled: true }]);
  };

  const hasInvalidEntry = emails.length === 0 || emails.some((e) => !isValidEmail(e.email));
  const hasEnabledEntry = emails.some((e) => e.enabled && isValidEmail(e.email));
  const canSave = !hasInvalidEntry && hasEnabledEntry && !updateSettings.isPending;

  const handleSave = async () => {
    if (hasInvalidEntry) {
      toast({ title: 'Validation error', description: 'Please enter a valid email for every entry', variant: 'destructive' });
      return;
    }
    if (!hasEnabledEntry) {
      toast({ title: 'Validation error', description: 'At least one email must be enabled', variant: 'destructive' });
      return;
    }

    const adminAlertEmails = emails.map((e) => ({ email: e.email.trim(), enabled: e.enabled }));

    try {
      await updateSettings.mutateAsync({ adminAlertEmails });
      setLocalEmails(null); // fall back to the now-updated cache value
      toast({ title: 'Settings saved', description: 'Settings updated successfully' });
    } catch {
      toast({ title: 'Error', description: 'Failed to save settings', variant: 'destructive' });
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 pb-[calc(env(safe-area-inset-bottom)+6rem)] sm:pb-12 [&>*:nth-child(odd):last-child]:lg:col-span-2">
      <Card>
        <CardHeader>
          <CardTitle>Energy Notifications</CardTitle>
          <CardDescription>
            Email addresses that receive an admin alert whenever a student exceeds their monthly energy limit.
            Disable an address to keep it on the list without sending it alerts.
            The price per excess kWh is configured per room in the Rooms section.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading settings…</p>
          ) : (
            <>
              <div className="space-y-3">
                <Label>Admin alert emails</Label>

                {emails.length === 0 && (
                  <p className="text-sm text-muted-foreground">
                    No emails configured. Add at least one to enable admin alerts.
                  </p>
                )}

                {emails.map((entry, index) => {
                  const showError = entry.email.trim() !== '' && !isValidEmail(entry.email);
                  return (
                    <div key={index} className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Input
                          type="email"
                          placeholder="admin@example.com"
                          value={entry.email}
                          onChange={(e) => handleEmailChange(index, e.target.value)}
                          aria-invalid={showError}
                          className={showError ? 'border-destructive' : undefined}
                        />
                        <div className="flex items-center gap-1.5 shrink-0">
                          <Switch
                            checked={entry.enabled}
                            onCheckedChange={(checked) => handleToggle(index, checked)}
                            aria-label={entry.enabled ? 'Disable email' : 'Enable email'}
                          />
                          <span className="text-xs text-muted-foreground w-12">
                            {entry.enabled ? 'Active' : 'Off'}
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="shrink-0 text-destructive"
                          onClick={() => handleRemove(index)}
                          title="Remove email"
                          aria-label="Remove email"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                      {showError && (
                        <p className="text-xs text-destructive">Enter a valid email address</p>
                      )}
                    </div>
                  );
                })}

                <div className="flex items-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAdd}
                    disabled={emails.length >= MAX_ALERT_EMAILS}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add email
                  </Button>
                  {emails.length >= MAX_ALERT_EMAILS && (
                    <span className="text-xs text-muted-foreground">
                      Maximum of {MAX_ALERT_EMAILS} emails reached.
                    </span>
                  )}
                </div>

                {emails.length > 0 && !hasInvalidEntry && !hasEnabledEntry && (
                  <p className="text-xs text-destructive">At least one email must be enabled.</p>
                )}
              </div>

              <Button onClick={handleSave} disabled={!canSave}>
                {updateSettings.isPending ? 'Saving…' : 'Save Settings'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Payment instructions</CardTitle>
          <CardDescription>
            Bank-transfer details shown on the booking success screen and in the confirmation email.
            Use the placeholders <code>{'{amount}'}</code> and <code>{'{reference}'}</code> in the
            instructions text. They are substituted with the booking amount and the booking ID.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoadingPayment ? (
            <p className="text-sm text-muted-foreground">Loading payment settings…</p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="payment-iban">IBAN</Label>
                  <Input
                    id="payment-iban"
                    value={paymentForm.iban}
                    onChange={(e) => handlePaymentChange('iban', e.target.value)}
                    onBlur={() =>
                      setPaymentForm((prev) => ({
                        ...prev,
                        iban: friendlyFormatIBAN(prev.iban) ?? prev.iban,
                      }))
                    }
                    placeholder="PT50 0000 0000 0000 0000 0000 0"
                    aria-invalid={showIbanError}
                    className={showIbanError ? 'border-destructive' : undefined}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  {showIbanError && (
                    <p className="text-xs text-destructive">Enter a valid IBAN (e.g. PT50 0000 0000 0000 0000 0000 0).</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="payment-holder">Account holder</Label>
                  <Input
                    id="payment-holder"
                    value={paymentForm.accountHolder}
                    onChange={(e) => handlePaymentChange('accountHolder', e.target.value)}
                    placeholder="Home2Students Lda."
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="payment-bank">Bank name</Label>
                  <Input
                    id="payment-bank"
                    value={paymentForm.bankName}
                    onChange={(e) => handlePaymentChange('bankName', e.target.value)}
                    placeholder="Caixa Geral de Depósitos"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="payment-label">First-payment label</Label>
                  <Input
                    id="payment-label"
                    value={paymentForm.firstPaymentLabel}
                    onChange={(e) => handlePaymentChange('firstPaymentLabel', e.target.value)}
                    placeholder="First month's rent"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="payment-instructions">Instructions shown to the student</Label>
                <Textarea
                  id="payment-instructions"
                  value={paymentForm.paymentInstructions}
                  onChange={(e) => handlePaymentChange('paymentInstructions', e.target.value)}
                  placeholder="Please transfer {amount} within 48h and use {reference} as the transfer description."
                  rows={5}
                />
                <p className="text-xs text-muted-foreground">
                  Supports <code>{'{amount}'}</code> and <code>{'{reference}'}</code>.
                </p>
              </div>

              <Button onClick={handleSavePayment} disabled={!canSavePayment}>
                {updatePaymentSettings.isPending ? 'Saving…' : 'Save payment settings'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Policies</CardTitle>
          <CardDescription>
            PDF documents shown to students on the public residence and room pages, directly under the booking call-to-action.
            Each policy applies to all residences.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {isLoadingPolicies ? (
            <p className="text-sm text-muted-foreground">Loading policies…</p>
          ) : (
            <>
              <PolicyUploadRow
                label="Cancellation policy"
                description="Cancellation terms shared across all residences."
                policyKey="cancellation"
                settings={policySettings}
                isUploading={uploadingKey === 'cancellation'}
                onPick={() => cancellationInputRef.current?.click()}
              />
              <input
                ref={cancellationInputRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={(e) => handlePolicyFileChange('cancellation', e)}
              />

              <PolicyUploadRow
                label="Landlord policies"
                description="Landlord and tenant rules shared across all residences."
                policyKey="landlord"
                settings={policySettings}
                isUploading={uploadingKey === 'landlord'}
                onPick={() => landlordInputRef.current?.click()}
              />
              <input
                ref={landlordInputRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={(e) => handlePolicyFileChange('landlord', e)}
              />
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Contact details</CardTitle>
          <CardDescription>
            Address, phone, and email shown in the site footer and on the public Contact page.
            The <code>tel:</code> and <code>mailto:</code> links are generated automatically from
            the phone and email.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoadingContact ? (
            <p className="text-sm text-muted-foreground">Loading contact details…</p>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="contact-address1">Address line 1</Label>
                  <Input
                    id="contact-address1"
                    value={contactForm.addressLine1}
                    onChange={(e) => handleContactChange('addressLine1', e.target.value)}
                    placeholder="Av. Duque de Ávila 95, 3º"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="contact-address2">Address line 2</Label>
                  <Input
                    id="contact-address2"
                    value={contactForm.addressLine2}
                    onChange={(e) => handleContactChange('addressLine2', e.target.value)}
                    placeholder="1000-139 Lisboa, Portugal"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="contact-phone">Phone</Label>
                  <Input
                    id="contact-phone"
                    value={contactForm.phone}
                    onChange={(e) => handleContactChange('phone', e.target.value)}
                    placeholder="+351 93 221 10 11"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="contact-email">Email</Label>
                  <Input
                    id="contact-email"
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => handleContactChange('email', e.target.value)}
                    placeholder="geral@home2students.com"
                    aria-invalid={showContactEmailError}
                    className={showContactEmailError ? 'border-destructive' : undefined}
                  />
                  {showContactEmailError && (
                    <p className="text-xs text-destructive">Enter a valid email address.</p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="contact-hours">Office hours</Label>
                  <Input
                    id="contact-hours"
                    value={contactForm.officeHours}
                    onChange={(e) => handleContactChange('officeHours', e.target.value)}
                    placeholder="Mon-Fri 9am-6pm"
                  />
                  <p className="text-xs text-muted-foreground">
                    Shown on the Contact page, under the phone number.
                  </p>
                </div>
              </div>

              <Button onClick={handleSaveContact} disabled={!canSaveContact}>
                {updateContactSettings.isPending ? 'Saving…' : 'Save contact details'}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

interface PolicyUploadRowProps {
  label: string;
  description: string;
  policyKey: PolicyKey;
  settings: PolicySettings | undefined;
  isUploading: boolean;
  onPick: () => void;
}

function PolicyUploadRow({ label, description, policyKey, settings, isUploading, onPick }: PolicyUploadRowProps) {
  const fileUrl = policyKey === 'cancellation' ? settings?.cancellationFileUrl : settings?.landlordFileUrl;
  const fileName = policyKey === 'cancellation' ? settings?.cancellationFileName : settings?.landlordFileName;

  return (
    <div className="space-y-2">
      <div>
        <Label>{label}</Label>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        {fileUrl ? (
          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline"
          >
            {fileName || 'View current PDF'}
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        ) : (
          <span className="text-sm text-muted-foreground">No file uploaded</span>
        )}
        <Button type="button" variant="outline" size="sm" onClick={onPick} disabled={isUploading}>
          <Upload className="h-4 w-4 mr-1" />
          {isUploading ? 'Uploading…' : fileUrl ? 'Replace PDF' : 'Upload PDF'}
        </Button>
      </div>
    </div>
  );
}

export default AdminSettings;
